import * as React from 'react';
import { useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { NotificationItem } from '../../reducers/notifications';
import { DrawerField } from './drawer/DrawerAtoms';
import { getDrawerFields } from './drawer/drawerCardConfig';
import './NotificationDrawer.scss';

interface Props {
    item: NotificationItem | null;
    locale: string;
    onClose: () => void;
}

/**
 * Right-anchored slide-over panel that shows a single notification's full
 * content without taking the user off the inbox page. The list row is the
 * one-glance summary; the drawer is where the user reads, decides, and
 * either ignores or clicks through to the underlying entity.
 *
 * Keyboard: Esc closes. Click on backdrop closes. Click on "View full page"
 * navigates to the action URL (and closes the drawer).
 *
 * Why a drawer instead of just navigating on click:
 *   - notification bodies in this app are text-heavy ("blocked because the
 *     warehouse has no MDF" can be 1-2 lines) and the row clamps to 2 lines.
 *     The drawer renders the body untruncated.
 *   - keeps the user in the inbox so they can read several in sequence
 *     without losing scroll / search / filter state.
 */
const NotificationDrawer: React.FC<Props> = ({ item, locale, onClose }) => {
    const history = useHistory();
    const intl = useIntl();

    // Lock body scroll while the drawer is open — desktop has its own scroll
    // inside the drawer, and bleeding scroll to the page underneath feels
    // janky.
    useEffect(() => {
        if (!item) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [item]);

    // Esc closes. Bound only while open so we don't fight other Esc handlers
    // on other pages.
    useEffect(() => {
        if (!item) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [item, onClose]);

    const handleViewFull = useCallback(() => {
        if (!item) return;
        const url = item.rendered_action_url;
        onClose();
        if (url) history.push(url);
    }, [item, onClose, history]);

    // Resolve the per-event-type field config BEFORE the null-check below so
    // hooks above this line stay stable even when the drawer is closed.
    // `getDrawerFields` is a pure function (no side effects) so calling it
    // unconditionally is safe; the rendered output is gated on `item`.
    const eventKey = item?.event?.event_type?.key;
    const fields = useMemo(() => getDrawerFields(eventKey), [eventKey]);
    const cardCtx = useMemo(() => ({ locale }), [locale]);

    if (!item) return null;

    const createdAt = moment(item.created_at).locale(locale);
    const relative = createdAt.isValid() ? createdAt.fromNow() : '';
    const absolute = createdAt.isValid() ? createdAt.format('DD MMM YYYY, HH:mm') : '';
    const priority = (item.priority || 'normal').toLowerCase();
    const showPriorityChip = priority === 'high' || priority === 'critical' || priority === 'urgent';

    return (
        <>
            <div className="notification-drawer__backdrop" onClick={onClose} aria-hidden="true" />
            <aside
                className="notification-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="notification-drawer-title"
            >
                <header className="notification-drawer__head">
                    <div className="notification-drawer__head-meta">
                        <span className="notification-drawer__time" title={absolute}>{relative}</span>
                        {showPriorityChip && (
                            <span className={`notification-drawer__priority notification-drawer__priority--${priority}`}>
                                <FormattedMessage
                                    id={`notifications.drawer.priority${priority.charAt(0).toUpperCase()}${priority.slice(1)}`}
                                    defaultMessage={priority}
                                />
                            </span>
                        )}
                        {item.pinned && (
                            <span className="notification-drawer__pinned">
                                <FormattedMessage id="notifications.drawer.pinned" defaultMessage="Pinned" />
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="notification-drawer__close"
                        onClick={onClose}
                        aria-label={intl.formatMessage({ id: 'notifications.drawer.close', defaultMessage: 'Close' })}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="6" y1="6" x2="18" y2="18" />
                            <line x1="18" y1="6" x2="6" y2="18" />
                        </svg>
                    </button>
                </header>

                <div className="notification-drawer__scroll">
                    <h2 id="notification-drawer-title" className="notification-drawer__title">
                        {item.rendered_title}
                    </h2>
                    <div className="notification-drawer__body">
                        {item.rendered_body || (
                            <span className="notification-drawer__body-empty">
                                <FormattedMessage id="notifications.drawer.noBody" defaultMessage="No additional details" />
                            </span>
                        )}
                    </div>

                    {item.rendered_image_url && (
                        <img
                            src={item.rendered_image_url}
                            alt=""
                            className="notification-drawer__image"
                        />
                    )}

                    <div className="drawer-card">
                        {fields.map((field, idx) => {
                            if (field.show && !field.show(item, cardCtx)) return null;
                            return (
                                <DrawerField
                                    key={`${field.labelId}-${idx}`}
                                    labelId={field.labelId}
                                    labelDefault={field.labelDefault}
                                >
                                    {field.render(item, cardCtx)}
                                </DrawerField>
                            );
                        })}
                    </div>
                </div>

                {item.rendered_action_url && (
                    <footer className="notification-drawer__foot">
                        <button
                            type="button"
                            className="notification-drawer__view-full"
                            onClick={handleViewFull}
                        >
                            <FormattedMessage id="notifications.drawer.viewFull" defaultMessage="View full page" />
                            <span aria-hidden="true" style={{ marginLeft: 8 }}>→</span>
                        </button>
                    </footer>
                )}
            </aside>
        </>
    );
};

export default NotificationDrawer;
